import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Loader2, Activity } from "lucide-react";
import SummaryCards from "@/components/command_center/SummaryCards";
import SourcesPanel from "@/components/command_center/SourcesPanel";
import TenantTable from "@/components/command_center/TenantTable";
import WarningsPanel from "@/components/command_center/WarningsPanel";
import V2RuntimeTab from "@/components/command_center/V2RuntimeTab";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "v2",       label: "V2 Runtime" },
];

export default function CommandCenterPage() {
  const { authAxios } = useAuth();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState(null);
  const [tab, setTab]             = useState("overview");

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get("/whatsapp/command-center/overview");
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Erro ao carregar Command Center";
      setError(msg);
      toast.error("Erro ao carregar Command Center");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <Activity className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => fetchData()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  const summary  = data?.summary  ?? {};
  const tenants  = data?.tenants  ?? [];
  const sources  = data?.sources  ?? {};
  const warnings = data?.warnings ?? [];
  const genAt    = data?.generated_at;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">WhatsApp Command Center</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Snapshot read-only · {genAt ? new Date(genAt).toLocaleString() : "—"}
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={refreshing} onClick={() => fetchData(true)}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tabDef => (
          <button
            key={tabDef.id}
            onClick={() => setTab(tabDef.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === tabDef.id
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tabDef.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <SummaryCards summary={summary} />
          <TenantTable  tenants={tenants} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SourcesPanel  sources={sources} />
            <WarningsPanel warnings={warnings} />
          </div>
        </>
      )}

      {tab === "v2" && <V2RuntimeTab />}
    </div>
  );
}
